import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
  Plus, 
  MoreHorizontal, 
  Pencil, 
  Trash2, 
  Upload,
  Loader2,
  MoveRight,
  Building2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SECTORS = ['Payments', 'Identity', 'Credit', 'Insurance', 'AI/Data', 'Healthcare', 'Other'];
const STAGES = ['Pre-seed', 'Seed', 'Series A', 'Series B', 'Growth'];

const PIPELINE_STAGES = [
  { id: 'sourcing', label: 'Sourcing', color: 'bg-slate-100 text-slate-700' },
  { id: 'due_diligence', label: 'Due Diligence', color: 'bg-amber-100 text-amber-700' },
  { id: 'term_sheet', label: 'Term Sheet', color: 'bg-blue-100 text-blue-700' },
  { id: 'closed', label: 'Closed', color: 'bg-purple-100 text-purple-700' },
];

const formatCurrency = (value) => {
  if (!value) return '-';
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  } else if (value >= 1000) {
    return `$${(value / 1000).toFixed(0)}K`;
  }
  return `$${value.toFixed(0)}`;
};

const emptyDeal = {
  company_name: '',
  sector: '',
  stage: '',
  potential_investment: '',
  target_ownership: '',
  description: '',
  contact_person: '',
  contact_email: '',
  deal_stage: 'sourcing',
  notes: '',
  expected_close_date: ''
};

const DealCard = ({ deal, onEdit, onDelete, onMoveStage }) => {
  const nextStage = PIPELINE_STAGES.find((s, i) => {
    const currentIndex = PIPELINE_STAGES.findIndex(ps => ps.id === deal.deal_stage);
    return i === currentIndex + 1;
  });

  return (
    <div className="kanban-card" data-testid={`deal-card-${deal.id}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <Building2 className="h-4 w-4 text-slate-400" />
          <h4 className="font-medium text-slate-900">{deal.company_name}</h4>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-6 w-6">
              <MoreHorizontal className="h-3 w-3" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onEdit(deal)}>
              <Pencil className="h-4 w-4 mr-2" />
              Edit
            </DropdownMenuItem>
            {nextStage && (
              <DropdownMenuItem onClick={() => onMoveStage(deal, nextStage.id)}>
                <MoveRight className="h-4 w-4 mr-2" />
                Move to {nextStage.label}
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={() => onDelete(deal)} className="text-red-600">
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="text-xs">{deal.sector}</Badge>
          <span className="text-xs text-slate-400">{deal.stage}</span>
        </div>
        
        <div className="flex justify-between text-sm">
          <span className="text-slate-500">Target</span>
          <span className="font-mono font-medium text-slate-700">
            {formatCurrency(deal.potential_investment)}
          </span>
        </div>
        
        <div className="flex justify-between text-sm">
          <span className="text-slate-500">Ownership</span>
          <span className="font-mono text-slate-700">{deal.target_ownership}%</span>
        </div>

        {deal.expected_close_date && (
          <div className="flex justify-between text-sm">
            <span className="text-slate-500">Expected Close</span>
            <span className="text-slate-700">{deal.expected_close_date}</span>
          </div>
        )}

        {deal.contact_person && (
          <p className="text-xs text-slate-400 mt-2">
            Contact: {deal.contact_person}
          </p>
        )}
      </div>
    </div>
  );
};

export default function DealPipelinePage() {
  const { getAuthHeaders } = useAuth();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [editingDeal, setEditingDeal] = useState(null);
  const [formData, setFormData] = useState(emptyDeal);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchDeals();
  }, []);

  const fetchDeals = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/deals`, {
        headers: getAuthHeaders()
      });
      setDeals(response.data);
    } catch (error) {
      toast.error('Failed to load deals');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (deal = null) => {
    if (deal) {
      setEditingDeal(deal);
      setFormData({
        company_name: deal.company_name || '',
        sector: deal.sector || '',
        stage: deal.stage || '',
        potential_investment: deal.potential_investment?.toString() || '',
        target_ownership: deal.target_ownership?.toString() || '',
        description: deal.description || '',
        contact_person: deal.contact_person || '',
        contact_email: deal.contact_email || '',
        deal_stage: deal.deal_stage || 'sourcing',
        notes: deal.notes || '',
        expected_close_date: deal.expected_close_date || ''
      });
    } else {
      setEditingDeal(null);
      setFormData(emptyDeal);
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.company_name || !formData.sector || !formData.stage) {
      toast.error('Please fill in required fields');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        potential_investment: parseFloat(formData.potential_investment) || 0,
        target_ownership: parseFloat(formData.target_ownership) || 0
      };

      if (editingDeal) {
        await axios.put(
          `${API_URL}/api/deals/${editingDeal.id}`,
          payload,
          { headers: getAuthHeaders() }
        );
        toast.success('Deal updated');
      } else {
        await axios.post(
          `${API_URL}/api/deals`,
          payload,
          { headers: getAuthHeaders() }
        );
        toast.success('Deal added');
      }
      
      setDialogOpen(false);
      fetchDeals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save deal');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (deal) => {
    if (!window.confirm(`Delete ${deal.company_name} from pipeline?`)) return;
    
    try {
      await axios.delete(`${API_URL}/api/deals/${deal.id}`, {
        headers: getAuthHeaders()
      });
      toast.success('Deal deleted');
      fetchDeals();
    } catch (error) {
      toast.error('Failed to delete deal');
    }
  };

  const handleMoveStage = async (deal, newStage) => {
    try {
      await axios.put(
        `${API_URL}/api/deals/${deal.id}`,
        { deal_stage: newStage },
        { headers: getAuthHeaders() }
      );
      toast.success(`Moved to ${newStage.replace('_', ' ')}`);
      fetchDeals();
    } catch (error) {
      toast.error('Failed to move deal');
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(
        `${API_URL}/api/import/deals`,
        formData,
        { 
          headers: { 
            ...getAuthHeaders(),
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      toast.success(response.data.message);
      setImportDialogOpen(false);
      fetchDeals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import CSV');
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const getDealsByStage = (stageId) => {
    return deals.filter(d => d.deal_stage === stageId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="pipeline-loading">
        <Loader2 className="h-8 w-8 animate-spin text-[#1E3A5F]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="pipeline-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 tracking-tight">Deal Pipeline</h1>
          <p className="text-slate-500 mt-1">{deals.length} deals in pipeline</p>
        </div>
        <div className="flex gap-3">
          <Button 
            variant="outline" 
            onClick={() => setImportDialogOpen(true)}
            data-testid="import-deals-btn"
          >
            <Upload className="h-4 w-4 mr-2" />
            Import CSV
          </Button>
          <Button 
            className="btn-primary" 
            onClick={() => handleOpenDialog()}
            data-testid="add-deal-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Deal
          </Button>
        </div>
      </div>

      {/* Kanban Board */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="kanban-board">
        {PIPELINE_STAGES.map((stage) => (
          <div key={stage.id} className="kanban-column" data-testid={`column-${stage.id}`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-semibold text-slate-900">{stage.label}</h3>
              <Badge className={stage.color}>{getDealsByStage(stage.id).length}</Badge>
            </div>
            <div className="space-y-3">
              {getDealsByStage(stage.id).map((deal) => (
                <DealCard
                  key={deal.id}
                  deal={deal}
                  onEdit={handleOpenDialog}
                  onDelete={handleDelete}
                  onMoveStage={handleMoveStage}
                />
              ))}
              {getDealsByStage(stage.id).length === 0 && (
                <div className="text-center py-8 text-slate-400 text-sm">
                  No deals
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="deal-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingDeal ? 'Edit Deal' : 'Add New Deal'}
            </DialogTitle>
            <DialogDescription>
              {editingDeal ? 'Update the deal details' : 'Enter the details of the new deal'}
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="col-span-2">
              <Label htmlFor="company_name">Company Name *</Label>
              <Input
                id="company_name"
                value={formData.company_name}
                onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                placeholder="Company name"
                data-testid="deal-company-name"
              />
            </div>
            
            <div>
              <Label htmlFor="sector">Sector *</Label>
              <Select 
                value={formData.sector} 
                onValueChange={(value) => setFormData({...formData, sector: value})}
              >
                <SelectTrigger data-testid="deal-sector">
                  <SelectValue placeholder="Select sector" />
                </SelectTrigger>
                <SelectContent>
                  {SECTORS.map(s => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="stage">Investment Stage *</Label>
              <Select 
                value={formData.stage} 
                onValueChange={(value) => setFormData({...formData, stage: value})}
              >
                <SelectTrigger data-testid="deal-stage">
                  <SelectValue placeholder="Select stage" />
                </SelectTrigger>
                <SelectContent>
                  {STAGES.map(s => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="potential_investment">Potential Investment ($)</Label>
              <Input
                id="potential_investment"
                type="number"
                value={formData.potential_investment}
                onChange={(e) => setFormData({...formData, potential_investment: e.target.value})}
                placeholder="500000"
                data-testid="deal-investment"
              />
            </div>

            <div>
              <Label htmlFor="target_ownership">Target Ownership (%)</Label>
              <Input
                id="target_ownership"
                type="number"
                step="0.1"
                value={formData.target_ownership}
                onChange={(e) => setFormData({...formData, target_ownership: e.target.value})}
                placeholder="25"
                data-testid="deal-ownership"
              />
            </div>

            <div>
              <Label htmlFor="deal_stage">Pipeline Stage</Label>
              <Select 
                value={formData.deal_stage} 
                onValueChange={(value) => setFormData({...formData, deal_stage: value})}
              >
                <SelectTrigger data-testid="deal-pipeline-stage">
                  <SelectValue placeholder="Select stage" />
                </SelectTrigger>
                <SelectContent>
                  {PIPELINE_STAGES.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="expected_close_date">Expected Close Date</Label>
              <Input
                id="expected_close_date"
                type="date"
                value={formData.expected_close_date}
                onChange={(e) => setFormData({...formData, expected_close_date: e.target.value})}
                data-testid="deal-close-date"
              />
            </div>

            <div>
              <Label htmlFor="contact_person">Contact Person</Label>
              <Input
                id="contact_person"
                value={formData.contact_person}
                onChange={(e) => setFormData({...formData, contact_person: e.target.value})}
                placeholder="John Doe"
                data-testid="deal-contact-person"
              />
            </div>

            <div>
              <Label htmlFor="contact_email">Contact Email</Label>
              <Input
                id="contact_email"
                type="email"
                value={formData.contact_email}
                onChange={(e) => setFormData({...formData, contact_email: e.target.value})}
                placeholder="john@company.com"
                data-testid="deal-contact-email"
              />
            </div>

            <div className="col-span-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Brief description..."
                rows={2}
                data-testid="deal-description"
              />
            </div>

            <div className="col-span-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Internal notes..."
                rows={2}
                data-testid="deal-notes"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              className="btn-primary" 
              onClick={handleSave}
              disabled={saving}
              data-testid="save-deal-btn"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {editingDeal ? 'Update' : 'Add'} Deal
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent data-testid="import-deals-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Import Deals from CSV</DialogTitle>
            <DialogDescription>
              Upload a CSV with columns: company_name, sector, stage, potential_investment, target_ownership, description, contact_person, contact_email, deal_stage, notes, expected_close_date
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <Input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleImport}
              disabled={importing}
              data-testid="deals-csv-input"
            />
          </div>

          {importing && (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-6 w-6 animate-spin text-[#1E3A5F]" />
              <span className="ml-2 text-slate-600">Importing...</span>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setImportDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
